import { FormItem, SwitchInput, TextInput, useField } from '@framework/form';
import { IDelegation } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { RowElementItem } from '@framework/ui';
import { Button, Col, Row, Space } from 'antd';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { EditModulesDelegationsValues } from './EditModulesDelegationsSchema';

import AddressBookButton from '#build-flags/routes/address-book/button/AddressBookButton';

export interface IModulesDelegationInputProps {
  icon: React.ReactNode;
  name: 'compilation' | 'sign';
  label: string;
  currentValue: IDelegation[];
  needConfirm: boolean;
  // TODO Aggiungere un tooltip che comunichi che non c'è firma per questi
  // moduli
  disabled?: boolean;
}

const TFormItem = FormItem as typeof FormItem<EditModulesDelegationsValues>;

/**
 * Componente di input per la gestione dei dati di delega dei moduli.
 * Se i moduli forniti hanno delegati differenti, mostra una maschera
 * che richiede la conferma esplicita per la modifica.
 */
export function ModulesDelegationInput(props: IModulesDelegationInputProps) {
  const { name, icon, label, currentValue, needConfirm, disabled } = props;

  const { t } = useTranslation();
  const enable = useWatch({ name: `${name}.enable` });
  const isConfirmed = useWatch({ name: `${name}.confirmUpdate` });
  const confirmField = useField(`${name}.confirmUpdate`);

  const helpText = t(
    'inserisci-cf-or-email-delegato',
    "Inserisci il Codice Fiscale o l'E-Mail per l'utente estero",
  );

  return (
    <Row>
      <Col span={10}>
        <TFormItem name={`${name}.enable`} layout={{ removeHelpMargin: true }}>
          <SwitchInput name={`${name}.enable`} disabled={disabled}>
            {icon}
          </SwitchInput>
        </TFormItem>
      </Col>
      <Col span={14}>
        {enable &&
          (needConfirm ? (
            <>
              <RowElementItem colSpan={4}>
                <TFormItem
                  name={`${name}.delegate`}
                  label={label}
                  help={
                    isConfirmed
                      ? helpText
                      : t(
                          'possibile-modificare-tutti-i-moduli-con-un-nuovo-delegato',
                          'È possibile modificare tutti i moduli con un nuovo delegato',
                        )
                  }
                >
                  <Space.Compact size="large" block>
                    <TextInput
                      name={`${name}.delegate`}
                      disabled={!isConfirmed}
                      placeholder={
                        !isConfirmed
                          ? t(
                              'delegati-differenti-o-mancanti',
                              'I moduli presentano delegati differenti o mancanti',
                            )
                          : undefined
                      }
                    />
                    {AddressBookButton && (
                      <AddressBookButton
                        idFieldName={`${name}.delegate`}
                        onSelect={(record, close, setValue) => {
                          setValue(
                            record.codiceFiscale ?? record.email ?? '<unknown>',
                          );
                          close();
                        }}
                      />
                    )}
                  </Space.Compact>
                </TFormItem>
              </RowElementItem>

              <Button
                block
                size="large"
                type={isConfirmed ? 'default' : 'primary'}
                onClick={() => confirmField.setValue(!isConfirmed)}
              >
                {isConfirmed
                  ? t('annulla-modifiche', 'Annulla modifiche')
                  : t('modifica-tutti', 'Modifica tutti')}
              </Button>

              {/* <ModulesPreviousDelegationsSummary delegations={currentValue} /> */}
            </>
          ) : (
            <TFormItem
              name={`${name}.delegate`}
              label={label}
              style={{ width: '100%' }}
              help={helpText}
            >
              <Space.Compact size="large" block>
                <TextInput
                  name={`${name}.delegate`}
                  style={{ width: '100%' }}
                />
                {AddressBookButton && (
                  <AddressBookButton
                    idFieldName={`${name}.delegate`}
                    onSelect={(record, close, setValue) => {
                      setValue(
                        record.codiceFiscale ?? record.email ?? '<unknown>',
                      );
                      close();
                    }}
                  />
                )}
              </Space.Compact>
            </TFormItem>
          ))}
      </Col>
    </Row>
  );
}
